import './style.scss';

export function header_block() {
  const header = document.querySelector('[data-site-header]');
  if (!header || header.dataset.rebredgNavigationReady === 'true') return;

  const toggle = header.querySelector('.rebredg-menu-toggle');
  const overlay = header.querySelector('.rebredg-menu-overlay');
  const navigation = overlay?.querySelector('.rebredg-menu-navigation');

  if (!toggle || !overlay || !navigation) return;

  header.dataset.rebredgNavigationReady = 'true';

  // The Site Editor uses a native Navigation block so labels and destinations
  // can be changed visually. Normalize its rendered markup back to the classes
  // used by the existing frontend animation/styles. This also keeps legacy
  // saved template parts working during the transition.
  const menuList = navigation.querySelector('.wp-block-navigation__container, .rebredg-menu-list');
  menuList?.classList.add('rebredg-menu-list');

  const menuLinks = [...navigation.querySelectorAll('a[href]')];
  menuLinks.forEach((link) => {
    link.classList.add('rebredg-menu-link');
    link.closest('li')?.classList.add('rebredg-menu-item');
  });

  const normalizePath = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      const path = url.pathname.replace(/\/+$/, '');
      return path || '/';
    } catch (error) {
      return '';
    }
  };

  let currentLink = menuLinks.find(
      (link) => link.getAttribute('aria-current') === 'page' || link.closest('.current-menu-item'),
  );

  if (!currentLink) {
    const currentPath = normalizePath(window.location.href);
    currentLink = menuLinks.find((link) => normalizePath(link.href) === currentPath);

    if (currentLink) {
      currentLink.setAttribute('aria-current', 'page');
    }
  }

  const bodyClasses = document.body.classList;

  if (!currentLink && (bodyClasses.contains('single-work') || bodyClasses.contains('tax-work_category'))) {
    currentLink = navigation.querySelector('.rebredg-menu-item--work a[href]');
  }

  if (
    !currentLink
    && (
      bodyClasses.contains('single-post')
      || bodyClasses.contains('category')
      || bodyClasses.contains('tag')
      || bodyClasses.contains('blog')
    )
  ) {
    currentLink = navigation.querySelector('.rebredg-menu-item--news a[href]');
  }

  currentLink?.classList.add('is-current');

  // The editor no longer has to store a decorative HTML span. Create the
  // indicator at runtime inside the navigation so it always tracks the
  // editable Navigation block correctly.
  let linkIndicator = navigation.querySelector('.rebredg-menu-link-indicator');
  if (!linkIndicator) {
    linkIndicator = document.createElement('span');
    linkIndicator.className = 'rebredg-menu-link-indicator';
    linkIndicator.setAttribute('aria-hidden', 'true');
    navigation.prepend(linkIndicator);
  }

  overlay.setAttribute('aria-hidden', 'true');
  overlay.setAttribute('inert', '');

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let isOpen = false;
  const root = document.documentElement;

  const setScrollbarCompensation = () => {
    const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
    root.style.setProperty('--rebredg-scrollbar-compensation', `${scrollbarWidth}px`);
  };

  const clearScrollbarCompensation = () => {
    root.style.removeProperty('--rebredg-scrollbar-compensation');
  };

  // One highlight, one owner.
  //
  // The circle and the link's own "lit" styling used to be driven by two
  // different things: the circle by JS (move on a link's mouseenter, reset only
  // when the pointer left the <nav>), the text by CSS :hover. They disagree
  // everywhere the pointer is inside the list but not on a link - the gaps
  // between the links - so crossing a gap left the circle parked on the link
  // you came from while the text highlight snapped back to the current page and
  // then back again. That flashing is the bug.
  //
  // So there is now a single piece of state - which link is highlighted - and
  // both the circle and the styling follow it. By construction they cannot
  // disagree.
  //
  // The list, not the <nav>, is the boundary. The nav is far taller than its
  // list (it carries the panel's top and bottom padding), so a pointer that
  // left the links but was still inside the nav fired no reset at all and the
  // circle stayed stuck on the last link hovered until the pointer left the
  // whole panel.
  let highlighted = null;
  // Set around openMenu()'s programmatic focus() so that one focus event does
  // not drag the highlight with it. focus() dispatches synchronously, so this
  // is only ever true for the duration of that single call.
  let suppressIndicatorFocus = false;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const {gsap} = window.rebredgVendors || {};

  // Tells the stylesheet that the classes below are being maintained, so it can
  // stop keying the highlight off :hover. Without JS the CSS keeps its own
  // :hover / .is-current fallback and behaves as it always did.
  menuList?.classList.add('is-highlight-managed');

  const moveIndicatorTo = (link) => {
    if (reducedMotion) return;

    const navRect = navigation.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const x = linkRect.left - navRect.left + linkRect.width / 2;
    const y = linkRect.top - navRect.top + linkRect.height / 2;

    if (gsap) {
      gsap.to(linkIndicator, {x, y, xPercent: -50, yPercent: -50, opacity: .35, duration: .22, ease: 'power2.out', overwrite: 'auto'});
    } else {
      linkIndicator.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      linkIndicator.style.opacity = '1';
    }
  };

  const hideIndicator = ({immediate = false} = {}) => {
    if (reducedMotion) return;

    if (gsap) {
      gsap.to(linkIndicator, {opacity: 0, duration: immediate ? 0 : .18, ease: 'power2.out', overwrite: 'auto'});
    } else {
      linkIndicator.style.opacity = '0';
    }
  };

  // `pointer` marks a highlight the reader asked for by pointing at or tabbing
  // to the link, as opposed to the resting one the current page gets for free.
  // Only the former is worth scaling up.
  const setHighlight = (link, {pointer = false, immediate = false} = {}) => {
    if (highlighted && highlighted !== link) {
      highlighted.classList.remove('is-highlighted', 'is-highlighted--pointer');
    }

    highlighted = link || null;

    if (!highlighted) {
      hideIndicator({immediate});
      return;
    }

    highlighted.classList.add('is-highlighted');
    highlighted.classList.toggle('is-highlighted--pointer', pointer);
    moveIndicatorTo(highlighted);
  };

  // Where the highlight belongs when nothing is being pointed at: on the
  // current page's link, or nowhere at all when this page is not in the menu.
  const restHighlight = ({immediate = false} = {}) => {
    setHighlight(currentLink || null, {immediate});
  };

  menuLinks.forEach((link) => {
    link.addEventListener('mouseenter', () => setHighlight(link, {pointer: true}));
    link.addEventListener('focus', () => {
      if (suppressIndicatorFocus) return;
      setHighlight(link, {pointer: true});
    });
  });

  // On the list: leaving a link for the gap below it is still "in the list", so
  // the highlight stays put and nothing flashes. It only goes back to the
  // current page once the pointer is off the links altogether.
  menuList?.addEventListener('mouseleave', () => restHighlight());
  menuList?.addEventListener('focusout', (event) => {
    if (!menuList.contains(event.relatedTarget)) {
      restHighlight();
    }
  });

  const getFocusableElements = () => [
    toggle,
    ...[...overlay.querySelectorAll(focusableSelector)].filter(
        (element) => element.getClientRects().length > 0,
    ),
  ];

  const openMenu = () => {
    if (isOpen) return;

    setScrollbarCompensation();
    isOpen = true;
    header.classList.add('is-navigation-open');
    document.documentElement.classList.add('rebredg-navigation-open');
    document.body.classList.add('rebredg-navigation-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');
    overlay.setAttribute('aria-hidden', 'false');
    overlay.removeAttribute('inert');

    const firstLink = currentLink || menuLinks[0];

    suppressIndicatorFocus = true;
    firstLink?.focus({preventScroll: true});
    suppressIndicatorFocus = false;

    restHighlight({immediate: true});
  };

  // `instant`: link clicks close with no reverse animation at all (the page
  // is navigating away anyway) — only the X/Escape close plays the reverse
  // of the open transition.
  const closeMenu = ({restoreFocus = true, instant = false} = {}) => {
    if (!isOpen) return;

    isOpen = false;

    if (instant) {
      header.classList.add('is-navigation-closing-instant');
    }

    header.classList.remove('is-navigation-open');
    document.documentElement.classList.remove('rebredg-navigation-open');
    document.body.classList.remove('rebredg-navigation-open');
    clearScrollbarCompensation();
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('inert', '');

    if (instant) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          header.classList.remove('is-navigation-closing-instant');
        });
      });
    }

    if (restoreFocus) {
      toggle.focus({preventScroll: true});
    }
  };

  toggle.addEventListener('click', () => {
    if (isOpen) {
      closeMenu();
      return;
    }

    openMenu();
  });

  menuLinks.forEach((link) => {
    link.addEventListener('click', () => closeMenu({restoreFocus: false, instant: true}));
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!firstElement || !lastElement) return;

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  });
}
